import * as React from "react";
import { CssVarsProvider, useColorScheme } from "@mui/joy/styles";
import GlobalStyles from "@mui/joy/GlobalStyles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormLabel from "@mui/joy/FormLabel";
import IconButton, { IconButtonProps } from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import { toast } from "react-toastify";
import { Link as RouterLink } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import Checkbox from "@mui/joy/Checkbox";
import Link from "@mui/joy/Link";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

function ColorSchemeToggle(props: IconButtonProps) {
  const { onClick, ...other } = props;
  const { mode, setMode } = useColorScheme();
  const [mounted, setMounted] = React.useState(false);
  
  React.useEffect(() => setMounted(true), []);
  return (
    <IconButton
      aria-label="toggle light/dark mode"
      size="sm"
      variant="outlined"
      disabled={!mounted}
      onClick={(event) => {
        setMode(mode === "light" ? "dark" : "light");
        onClick?.(event);
      }}
      {...other}
    >
      {mode === "light" ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
    </IconButton>
  );
}

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  terms?: string;
}

interface TouchedFields {
  name?: boolean;
  email?: boolean;
  password?: boolean;
  confirmPassword?: boolean;
  terms?: boolean;
}

export default function SignUp() {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const { signUp } = UserAuth();
  const [registrationComplete, setRegistrationComplete] = React.useState(false);
  const [agreeToTerms, setAgreeToTerms] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = React.useState<TouchedFields>({});
  const [showPassword, setShowPassword] = React.useState(false);

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const errors: FieldErrors = {};

    // Validation checks with specific field errors
    if (!trimmedEmail) {
      errors.email = "Email is required.";
    }
    if (!trimmedName) {
      errors.name = "Username is required.";
    } else if (!/^[a-zA-Z0-9]+$/.test(trimmedName)) {
      errors.name = "Username must be alphanumeric.";
    }
    
    if (!trimmedPassword) {
      errors.password = "Password is required.";
    } else {
      const passwordErrors: string[] = [];
      if (trimmedPassword.length < 8) passwordErrors.push("at least 8 characters");
      if (!/[a-z]/.test(trimmedPassword)) passwordErrors.push("one lowercase letter");
      if (!/[A-Z]/.test(trimmedPassword)) passwordErrors.push("one uppercase letter");
      if (!/\d/.test(trimmedPassword)) passwordErrors.push("one number");
      if (!/[@$!%*?&]/.test(trimmedPassword)) passwordErrors.push("one special character");
      
      if (passwordErrors.length > 0) {
        errors.password = `Password must include ${passwordErrors.join(", ")}.`;
      }
    }

    if (!trimmedConfirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (trimmedPassword !== trimmedConfirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (!agreeToTerms) {
      errors.terms = "You must agree to the terms and conditions.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Show all field errors on submit by marking all fields as touched
    setTouchedFields({
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
      terms: true
    });

    // Perform validation when the form is submitted
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    try {
      await signUp(email.trim(), password.trim(), name.trim(), agreeToTerms);
      setRegistrationComplete(true);
      toast.success(
        "Registration successful! Please check your email to verify your account."
      );
    } catch (error: any) {
      toast.error(error.message || "Something went wrong. Please try again.");
    }
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleBlur = (field: keyof TouchedFields) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    validateForm(); // Validate on blur
  };

  React.useEffect(() => {
    // Only check form validity on form field changes when user has not yet submitted.
    setIsFormValid(validateForm());
  }, [email, name, password, confirmPassword, agreeToTerms]);

  if (registrationComplete) {
    return (
      <CssVarsProvider defaultMode="dark" disableTransitionOnChange>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            textAlign: "center",
          }}
        >
          <Typography level="h4" component="h1" gutterBottom>
            Registration Successful!
          </Typography>
          <Typography level="body-md" sx={{ mb: 4 }}>
            Please check your email and verify your account before signing in.
          </Typography>
          <Button component={RouterLink} to="/signin" variant="outlined">
            Go to Sign In
          </Button>
        </Box>
      </CssVarsProvider>
    );
  }

  return (
    <CssVarsProvider defaultMode="dark" disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ":root": {
            "--Form-maxWidth": "800px",
            "--Transition-duration": "0.4s",
          },
        }}
      />
      <Box
        sx={(theme) => ({
          width: { xs: "100%", md: "50vw" },
          transition: "width var(--Transition-duration)",
          transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "flex-end",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(255 255 255 / 0.2)",
          [theme.getColorSchemeSelector("dark")]: {
            backgroundColor: "rgba(19 19 24 / 0.4)",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
            width: "100%",
            px: 2,
          }}
        >
          <Box
            component="header"
            sx={{
              py: 3,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
              <IconButton size="sm" component={RouterLink} to="/">
                <img
                  src={process.env.REACT_APP_LOGO}
                  alt="Icon"
                  height="33px"
                  width="33px"
                />
              </IconButton>
              <Typography level="title-lg">
                {process.env.REACT_APP_NAME}
              </Typography>
            </Box>
            <ColorSchemeToggle />
          </Box>
          <Box
            component="main"
            sx={{
              my: "auto",
              py: 1,
              pb: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: 400,
              maxWidth: "100%",
              mx: "auto",
              borderRadius: "sm",
              "& form": {
                display: "flex",
                flexDirection: "column",
                gap: 2,
              },
              [`& .MuiFormLabel-asterisk`]: {
                visibility: "hidden",
              },
            }}
          >
            <Stack gap={4} sx={{ mb: 2 }}>
              <Stack gap={0.5}>
                <Typography component="h1" level="h3">
                  Create an account
                </Typography>
                <Typography level="body-sm">
                  Sign up to get started with {process.env.REACT_APP_NAME}
                </Typography>
              </Stack>
              <Stack gap={3} sx={{ mb: 2 }}>
                <form onSubmit={handleSignUp}>
                  <FormLabel>Username</FormLabel>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Enter your username"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                    onBlur={() => handleBlur('name')}
                    error={touchedFields.name && !!fieldErrors.name}
                    required
                  />
                  {touchedFields.name && fieldErrors.name && (
                    <Typography level="body-xs" color="danger">
                      {fieldErrors.name}
                    </Typography>
                  )}

                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    onBlur={() => handleBlur('email')}
                    error={touchedFields.email && !!fieldErrors.email}
                    required
                  />
                  {touchedFields.email && fieldErrors.email && (
                    <Typography level="body-xs" color="danger">
                      {fieldErrors.email}
                    </Typography>
                  )}

                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPassword(e.target.value)
                    }
                    onBlur={() => handleBlur('password')}
                    error={touchedFields.password && !!fieldErrors.password}
                    required
                  />
                  {touchedFields.password && fieldErrors.password && (
                    <Typography level="body-xs" color="danger">
                      {fieldErrors.password}
                    </Typography>
                  )}

                  <FormLabel>Confirm Password</FormLabel>
                  <Input
                    // type="password"
                    type={showPassword ? "text" : "password"}
                    name="confirm-password"
                    placeholder="Confirm your password"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setConfirmPassword(e.target.value)
                    }
                    onBlur={() => handleBlur('confirmPassword')}
                    error={touchedFields.confirmPassword && !!fieldErrors.confirmPassword}
                    required
                    endDecorator={
                      <IconButton
                        size="sm"
                        onClick={toggleShowPassword}
                        sx={{ ml: 1 }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    }
                  />
                  {touchedFields.confirmPassword && fieldErrors.confirmPassword && (
                    <Typography level="body-xs" color="danger">
                      {fieldErrors.confirmPassword}
                    </Typography>
                  )}

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={(e) => {
                        setAgreeToTerms(e.target.checked);
                        setTouchedFields(prev => ({...prev, terms: true}));
                      }}
                    />
                    <Typography component="span" level="body-sm">
                      I have read and agree to the{" "}
                      <Link
                        href="/termsofservice"
                        underline="hover"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        terms and conditions
                      </Link>
                      .
                      <span style={{ color: "red" }}> *</span>
                    </Typography>
                  </Stack>
                  {touchedFields.terms && fieldErrors.terms && (
                    <Typography level="body-xs" color="danger">
                      {fieldErrors.terms}
                    </Typography>
                  )}

                  <Stack gap={4} sx={{ mt: 2 }}>
                    <Button type="submit" variant="plain" fullWidth>
                      Sign Up
                    </Button>
                  </Stack>
                </form>
              </Stack>
            </Stack>
          </Box>
          <Box component="footer" sx={{ py: 3 }}>
            <Typography level="body-xs" textAlign="center">
              © HackTheWay {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          height: "100%",
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          left: { xs: 0, md: "50vw" },
          transition:
            "background-image var(--Transition-duration), left var(--Transition-duration) !important",
          transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
          backgroundColor: "background.level1",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundImage:
            "url(https://firebasestorage.googleapis.com/v0/b/ctfplatform-8e868.appspot.com/o/light_mode.png?alt=media&token=6c9999ff-521b-4d12-9c49-6eb3c59e82a8)",
          [theme.getColorSchemeSelector("dark")]: {
            backgroundImage:
              "url(https://images.unsplash.com/photo-1562813733-b31f71025d54?q=80&w=1769&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)",
          },
        })}
      />
    </CssVarsProvider>
  );
}
